import { ElementType, ForwardedRef, forwardRef } from 'react';

import { Card, CardActionArea, CardActionAreaProps, CardProps, Stack } from '@mui/material';

import { Offering } from '@arrived/models';

import { PropertyGridCardContent } from './PropertyGridCardContent';
import { PropertyGridCardLeftContent } from './PropertyGridCardLeftContent';
import { PropertyGridCardRightContent } from './PropertyGridCardRightContent';
import { PropertyGridCardStatusFlag } from './PropertyGridCardStatusFlag';
import { PropertyGridCardThumbnail } from './PropertyGridCardThumbnail';

export interface PropertyGridCardProps<D extends ElementType> extends Omit<CardProps, 'ref'> {
  cardActionAreaProps?: CardActionAreaProps<D>;
  offering: Offering;
  onInvestNowClick: (content?: string) => void;
}

const PropertyGridCardInner = <D extends ElementType>(
  { cardActionAreaProps, children, offering, onInvestNowClick, sx, ...rest }: PropertyGridCardProps<D>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const { sx: cardActionAreaSx, ...cardActionAreaRest } = cardActionAreaProps || {};

  return (
    <Card
      sx={{
        border: ({ palette }) => `1px solid ${palette.neutrals.platinum}`,
        overflow: 'hidden',
        '&:hover': { border: ({ palette }) => `1px solid ${palette.neutrals.gray}` },
        position: 'relative',
        transition: 'border 250ms',
        height: '100%',
        display: 'flex',
        ...sx,
      }}
      ref={ref}
      {...rest}
    >
      <CardActionArea
        sx={{
          '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          ...cardActionAreaSx,
        }}
        {...cardActionAreaRest}
      >
        <PropertyGridCardThumbnail offering={offering} />
        <PropertyGridCardContent offering={offering} sx={{ p: 2 }} />
        <Stack
          sx={({ breakpoints }) => ({
            alignItems: 'flex-end',
            flexDirection: 'row',
            // There are specific breakpoints in which the left and right sections collide, these breakpoints
            // specifically accomodate that collision point.
            [breakpoints.up('lg')]: { alignItems: 'flex-start', flexDirection: 'column' },
            // If we don't do between here, then this style will override the 'xl' style below it.
            [breakpoints.between(1190, 'xl')]: { alignItems: 'flex-end', flexDirection: 'row' },
            [breakpoints.up('xl')]: { alignItems: 'flex-start', flexDirection: 'column' },
            [breakpoints.up(1560)]: { alignItems: 'flex-end', flexDirection: 'row' },
          })}
          flex={1}
          width="100%"
          justifyContent="space-between"
          pb={2}
          px={2}
          flexWrap="wrap"
          // Height will be 52px if there is a button, by making the minimum 52px we'll get the same height even if
          // there is no button.
          minHeight="52px"
          columnGap={2}
          rowGap={1}
          // Position relative and a zIndex > 1 allow the child elements to be clicked over the action area of the card.
          position="relative"
          zIndex={1}
        >
          <PropertyGridCardLeftContent offering={offering} />
          <PropertyGridCardRightContent offering={offering} onInvestNowClick={onInvestNowClick} />
        </Stack>
        {children}
      </CardActionArea>
      <PropertyGridCardStatusFlag
        offeringId={offering.id}
        top={({ spacing }) => spacing(2)}
        right={({ spacing }) => spacing(2)}
        sx={{ position: 'absolute' }}
      />
    </Card>
  );
};

export const PropertyGridCard = forwardRef(PropertyGridCardInner);
